<template>
  <div
    v-if="showCustomContent"
    class="seo-content"
    :class="{
      'bg-grey': route.fullPath === '/' || route.fullPath === `/${locale}`,
    }"
  >
    <BaseCustomHTMLContent
      class="content container"
      :custom-html-content="data?.websitePage?.content"
    />
  </div>

  <FaqBlock :title="faq?.faqTitle" :faqs="faq?.faqs" v-if="faq?.faqs" />
</template>

<script setup lang="ts">
import { useWebsitePageQuery } from "~/graphql/generated";

const getRouteBaseName = useRouteBaseName();
// Watch for route changes to set the correct page title on navigating
const route = useRoute();

const isCustomPage = computed(() => getRouteBaseName(route) === "slug");

// Retrieve the SEO text block and FAQ for the current path, useWebsitePageQuery should be cached
// because `DynamicContent.vue` can also call it.
const { websitePagePath } = useWebsitePagePath(() => route.path);
const { data, executeQuery } = await useWebsitePageQuery({
  variables: websitePagePath,
});

// Watch the locale and refetch the page when it changes
const { locale } = useI18n();
watch(locale, () => {
  executeQuery({ fetchPolicy: "network-only" });
});

const showCustomContent = computed(() => {
  if (isCustomPage.value) return false;
  return !isContentBlockEmpty(data.value?.websitePage?.content);
});

const faq = computed(() => {
  const websitePage = data.value?.websitePage;
  if (!websitePage || !("contentBlocks" in websitePage)) return null;

  return {
    // Prefer the dedicated field. Older pages set the title as contentBlocks[5] instead.
    faqTitle: websitePage.titleQuestionsAnswers || websitePage.contentBlocks[5],
    // The FAQ section is independent of how many, if any, contentBlocks exist.
    faqs: websitePage.contentQuestionsAnswers.length
      ? websitePage.contentQuestionsAnswers
      : undefined,
  };
});

useHead({
  ...(data.value?.websitePage?.title && {
    title: data.value?.websitePage?.title,
  }),
  ...(data.value?.websitePage?.metaDescription && {
    meta: [
      {
        name: "description",
        content: data.value.websitePage.metaDescription,
      },
    ],
  }),
});
</script>

<style lang="scss" scoped>
.content {
  margin-top: 3rem;
  margin-bottom: 2rem;
}

.bg-grey {
  background-color: $light-95;
}
</style>
